<template>
  <bar-chart
    :title="title"
    :colors="colors"
    :series="series"
    :labels="labels"
    :loading="loading"
    :datalabels="false"
    :yAxis="yAxis"
    :formatter="formatter"
    :outlined="true"
    :dags="
      useMarmitonFilters
        ? [
            'get_and_store_posts_marmiton_ga_daily_dag',
            'adn_daily_posts_sites_infos_dag',
          ]
        : ['ga_get_and_store_site_daily_dag', 'adn_daily_posts_sites_infos_dag']
    "
    useMarmitonFilters
  />
</template>

<script>
import debounce from "lodash/debounce";

import BarChart from "@/components/common/charts/BarChart";
import { ingredientListFromAutoCompleteValues } from "@/components/regie/common/utils/ingredientGroup";

import useAxios from "@/hooks/useAxios";
import { colors } from "@/utils/constants";
import { formatNumber, formatPercent } from "@/utils/formatting";

const emptySeries = [
  {
    name: "Vues",
    data: [],
  },
];

export default {
  name: "ViewsByDay",
  props: {
    title: {
      type: String,
      require: true,
    },
    department: {
      type: String,
      required: true,
      validator(value) {
        return ["marmiton", "regie"].includes(value);
      },
    },
  },
  components: {
    BarChart,
  },
  data() {
    return {
      loading: false,
      colors: [colors.views],
      series: [...emptySeries],
      labels: [],
      formatter: (val) => {
        return this.formatData(val);
      },
      yAxis: [
        {
          min: 0,
          forceNiceScale: true,
          labels: {
            formatter: (val) => this.formatData(val),
          },
        },
      ],
    };
  },
  setup() {
    const { axiosGet } = useAxios();

    return {
      axiosGet,
    };
  },
  created() {
    this.debouncedGetData();
  },
  methods: {
    resetData() {
      this.series = [...emptySeries];
      this.labels = [];
    },
    debouncedGetData: debounce(async function () {
      await this.getData();
    }, 500),
    formatData(val) {
      return this.useMarmitonFilters ? formatPercent(val) : formatNumber(val);
    },
    formatResult(data) {
      if (!this.useMarmitonFilters) {
        return data;
      }

      const max = Math.max(...data);

      return data.map((val) => val / max);
    },
    getParams() {
      if (this.useMarmitonFilters) {
        return {
          start_date: this.startDate,
          end_date: this.endDate,
          ingredients_to_include: this.ingredientsToInclude,
          ingredients_to_exclude: "",
          utensils: this.utensilsToInclude,
          search_text: this.searchText,
          ...(this.flavour ? { flavour: this.flavour } : {}),
          ...(this.region ? { region: this.region } : {}),
          ...(this.ageSlot ? { ageSlot: this.ageSlot } : {}),
          ...(this.dish ? { dish: this.dish } : {}),
          ...(this.cost ? { cost: this.cost } : {}),
          ...(this.duration ? { duration: this.duration } : {}),
          ...(this.rating ? { rating: this.rating } : {}),
          ...(this.rates ? { rates: this.rates } : {}),
        };
      }

      return {
        start_date: this.startDate,
        end_date: this.endDate,
        sites_id: this.sites.join(","),
        search_text: this.searchText,
      };
    },
    async getData() {
      if (
        this.applyFilters &&
        !this.loading &&
        this.sites &&
        this.sites.length > 0 &&
        this.startDate &&
        this.startDate.length === 10 &&
        this.endDate &&
        this.endDate.length === 10
      ) {
        this.loading = true;
        const params = this.getParams();
        const { data } = await this.axiosGet(
          this.useMarmitonFilters
            ? "/regie/marmiton/sessions-by-days"
            : "/adn-post/sessions-by-days",
          params
        );

        const series = [];
        const labels = [];
        for (const { date, views } of data) {
          series.push(views);
          labels.push(date);
        }

        this.series = [
          {
            name: "Vues",
            data: this.formatResult(series),
          },
        ];
        this.labels = labels;

        this.loading = false;

        // Force clicking on button again to re-run that request :
        // (we don't call this dispatch earlier to be sure, as state is async, that all listening components could run their requests)
        this.$store.dispatch("common/updateApplyFilters", false);
      }
    },
  },
  computed: {
    applyFilters() {
      return this.$store.getters["common/getApplyFilters"];
    },
    sites() {
      return this.$store.getters[`${this.department}/getSites`];
    },
    startDate() {
      return this.$store.getters[`${this.department}/getDates`][0];
    },
    endDate() {
      return this.$store.getters[`${this.department}/getDates`][1];
    },
    flavour() {
      return this.useMarmitonFilters
        ? this.$store.getters["marmiton/getFlavour"]
        : "";
    },
    region() {
      return this.useMarmitonFilters
        ? this.$store.getters["marmiton/getRegion"]
        : "";
    },
    ageSlot() {
      return this.useMarmitonFilters
        ? this.$store.getters["marmiton/getAgeSlot"]
        : "";
    },
    ingredientsToInclude() {
      const ingredients = this.useMarmitonFilters
        ? this.$store.getters["marmiton/getMarmitonIngredientsToInclude"]
        : [];

      return ingredientListFromAutoCompleteValues(ingredients).join(",");
    },
    utensilsToInclude() {
      return this.useMarmitonFilters
        ? this.$store.getters["marmiton/getMarmitonUtensilsToInclude"].join(",")
        : "";
    },
    searchText() {
      return this.$store.getters[`${this.department}/getContainsText`];
    },
    useMarmitonFilters() {
      return ["marmiton"].includes(this.department);
    },
    dish() {
      return this.useMarmitonFilters
        ? this.$store.getters["marmiton/getDish"]
        : "";
    },
    cost() {
      return this.useMarmitonFilters
        ? this.$store.getters["marmiton/getCost"]
        : "";
    },
    duration() {
      return this.useMarmitonFilters
        ? this.$store.getters["marmiton/getDuration"]
        : 0;
    },
    rating() {
      return this.useMarmitonFilters
        ? this.$store.getters["marmiton/getRating"]
        : 0;
    },
    rates() {
      return this.useMarmitonFilters
        ? this.$store.getters["marmiton/getRates"]
        : 0;
    },
  },
  watch: {
    applyFilters() {
      this.debouncedGetData();
    },
    department() {
      this.resetData();
    },
  },
};
</script>
